import { store } from '../../store';
import { WEEK_DAYS_SLUGS } from 'constants/date-constants';

export const getTimezone = () => {
  try {
    return store.getState()?.userData?.data?.time_zone || undefined;
  } catch {
    return undefined;
  }
};

export default function DisplayDateTime(TimeStamp) {
  if (!TimeStamp) {
    return null;
  }

  let date = new Date(TimeStamp);
  let currentDate = new Date();
  let timeDifference = Math.abs(currentDate.getTime() - date.getTime());

  if (timeDifference < 60 * 1000) {
    return 'now';
  } else {
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
      timeZone: getTimezone(),
    });
  }
}

export const DisplayDate = (TimeStamp, checkTimezone = true) => {
  if (!TimeStamp) {
    return null;
  }

  let date = new Date(TimeStamp);
  let currentDate = new Date();
  let timeDifference = Math.abs(currentDate.getTime() - date.getTime());

  if (timeDifference < 60 * 1000) {
    return 'now';
  } else {
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      ...(checkTimezone ? { timeZone: getTimezone() } : {}),
    });
  }
};

export const DisplayTime = (TimeStamp) => {
  if (!TimeStamp) {
    return null;
  }

  let date = new Date(TimeStamp);
  let currentDate = new Date();
  let timeDifference = Math.abs(currentDate.getTime() - date.getTime());

  if (timeDifference < 60 * 1000) {
    return 'now';
  } else {
    return date.toLocaleString('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
      timeZone: getTimezone(),
    });
  }
};

export const formatChatTimestamp = (timestamp) => {
  const now = new Date();
  const time = new Date(timestamp);
  const diffMs = now.getTime() - time.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

  const formatter = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: getTimezone(),
  });

  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes} mins ago`;
  if (diffHours < 24 && now.toDateString() === time.toDateString()) return `${diffHours} hours ago`;

  const yesterday = new Date();
  yesterday.setDate(now.getDate() - 1);
  if (time.toDateString() === yesterday.toDateString()) return `Yesterday`;

  const tomorrow = new Date();
  tomorrow.setDate(now.getDate() + 1);
  if (time.toDateString() === tomorrow.toDateString()) return `Tomorrow`;

  return time.toLocaleDateString('en-GB', { timeZone: getTimezone() });
};

export const DisplayChatDate = (TimeStamp) => {
  if (!TimeStamp) {
    return null;
  }

  const timezone = getTimezone();
  const date = new Date(TimeStamp);
  const currentDate = new Date();

  // Compare dates in the user's timezone
  const toDateParts = (d) =>
    new Intl.DateTimeFormat('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: timezone }).format(
      d,
    );

  const isSameDay = toDateParts(date) === toDateParts(currentDate);

  if (isSameDay) {
    return 'Today';
  } else {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: timezone,
    });
  }
};

export const DisplayDateTimeDay = (TimeStamp) => {
  if (!TimeStamp) {
    return null;
  }

  let date = new Date(TimeStamp);
  let currentDate = new Date();
  let timeDifference = Math.abs(currentDate.getTime() - date.getTime());

  if (timeDifference < 60 * 1000) {
    return 'now';
  } else {
    return date.toLocaleString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
      timeZone: getTimezone(),
    });
  }
};

export const DisplayDateMonth = (TimeStamp) => {
  if (!TimeStamp) {
    return null;
  }

  let date = new Date(TimeStamp);
  let currentDate = new Date();
  let timeDifference = Math.abs(currentDate.getTime() - date.getTime());

  if (timeDifference < 60 * 1000) {
    return 'now';
  } else {
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone: getTimezone(),
    });
  }
};

export const convertTimeToTimezone = (time, timezone = getTimezone()) => {
  if (!time || Number.isNaN(new Date(time).getTime())) return null;

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(new Date(time));

  const values = Object.fromEntries(
    parts.filter(({ type }) => type !== 'literal').map(({ type, value }) => [type, value]),
  );

  return `${values.year}-${values.month}-${values.day}T${values.hour}:${values.minute}:${values.second}`;
};

export const dateKeyInTimezone = (time, timezone = getTimezone()) =>
  convertTimeToTimezone(time, timezone)?.split('T')[0] ?? null;

export const convertTimeFromTimezone = (localTimeString, timezone = getTimezone()) => {
  // Strip any trailing offset/Z, e.g. "+05:00", "-08:00", "Z" — we ignore it entirely
  const cleaned = localTimeString.replace(/(Z|[+-]\d{2}:\d{2})$/, '');

  const [datePart, timePart] = cleaned.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hour, minute, second] = timePart.split(':').map(Number);

  const asUTC = Date.UTC(year, month - 1, day, hour, minute, second);

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(new Date(asUTC));

  const values = Object.fromEntries(
    parts.filter(({ type }) => type !== 'literal').map(({ type, value }) => [type, value]),
  );

  const renderedAsUTC = Date.UTC(
    Number(values.year),
    Number(values.month) - 1,
    Number(values.day),
    Number(values.hour),
    Number(values.minute),
    Number(values.second),
  );

  const offset = renderedAsUTC - asUTC;

  return new Date(asUTC - offset);
};

const WEEKDAY_ORDER = Object.values(WEEK_DAYS_SLUGS); // ['monday' ... 'sunday']

// Weekday slug ('monday' ... 'sunday') of an instant as seen in a timezone.
export const getWeekdayInTimezone = (time, timezone = getTimezone()) =>
  new Intl.DateTimeFormat('en-US', { timeZone: timezone, weekday: 'long' }).format(new Date(time)).toLowerCase();

// A stored shift is a *recurring weekly* event: `day_of_week` is the anchor weekday
// and start_time/end_time carry only a meaningful time-of-day (the stored calendar
// date is a frozen placeholder, e.g. 2000-01-01). Reconstruct the shift's real
// start/end instants by placing that time-of-day onto the matching weekday of the
// CURRENT week, so the moment converts correctly - both the day and DST - into any
// viewer timezone.
export const getRecurringShiftInstants = (shift = {}) => {
  const start = new Date(shift?.start_time);
  const end = new Date(shift?.end_time);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return { start, end };
  }

  const DAY_MS = 24 * 60 * 60 * 1000;

  // Calculate duration based on time-of-day.
  // Recurring shifts may use the same dummy date for both
  // start and end, e.g.
  //
  // 2000-01-01 11:00 → 2000-01-01 00:00
  //
  // which actually means:
  //
  // Monday 11:00 → Tuesday 00:00
  const startMinutes = start.getUTCHours() * 60 + start.getUTCMinutes() + start.getUTCSeconds() / 60;

  const endMinutes = end.getUTCHours() * 60 + end.getUTCMinutes() + end.getUTCSeconds() / 60;

  let durationMs = (endMinutes - startMinutes) * 60 * 1000;

  // End time is earlier than start time → overnight shift.
  if (durationMs < 0) {
    durationMs += DAY_MS;
  }

  const now = new Date();

  // Monday of the current UTC week.
  const mondayOffset = (now.getUTCDay() + 6) % 7;

  const mondayMs = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - mondayOffset);

  const dayIndex = Math.max(0, WEEKDAY_ORDER.indexOf(String(shift?.day_of_week || '').toLowerCase()));

  const anchor = new Date(mondayMs + dayIndex * DAY_MS);

  // Rebase the recurring shift's time onto the
  // correct weekday of the current week.
  const rebasedStart = new Date(
    Date.UTC(
      anchor.getUTCFullYear(),
      anchor.getUTCMonth(),
      anchor.getUTCDate(),
      start.getUTCHours(),
      start.getUTCMinutes(),
      start.getUTCSeconds(),
      start.getUTCMilliseconds(),
    ),
  );

  const rebasedEnd = new Date(rebasedStart.getTime() + durationMs);

  return {
    start: rebasedStart,
    end: rebasedEnd,
  };
};

// Weekday slug ('monday' ... 'sunday') of an instant in UTC - the canonical anchor
// stored back with a shift so getRecurringShiftInstants round-trips it exactly.
export const getUtcWeekday = (time) => WEEKDAY_ORDER[(new Date(time).getUTCDay() + 6) % 7];
